/**
 * ClickHouseSetupPanel — the grant script for the values typed in the ClickHouse account form.
 * Regenerates as the admin types; shows nothing until the database and username are valid.
 */
import { WORKING_DATABASE_RULE } from '@server/clickhouse-grants';
import * as React from 'react';

import { CopyButton } from '@/_shared/components/CopyButton';
import { ButtonGroup } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { useSyntaxStyle } from '@/_shared/hooks/useSyntaxStyle';
import { codeBlockCustomStyle, SyntaxHighlighter } from '@/_shared/lib/syntax';

import {
  CLICKHOUSE_SETUP_DESCRIPTION,
  type ClickHouseSetupSQLParams,
  generateClickHouseSetupSQL,
} from '../../../lib/clickhouse-setup';
import { DownloadButton } from '../shared/DownloadButton';
import { OutputSection } from '../shared/OutputSection';

interface ClickHouseSetupPanelProps {
  values: ClickHouseSetupSQLParams;
}

export function ClickHouseSetupPanel({ values }: ClickHouseSetupPanelProps) {
  const syntaxStyle = useSyntaxStyle();
  const result = React.useMemo((): { sql: string } | { error: string } => {
    try {
      return { sql: generateClickHouseSetupSQL(values) };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Setup SQL could not be generated' };
    }
  }, [values]);

  if ('error' in result) {
    // The fields explain a bad value, so an untouched form shows no error here. The one rule no field
    // checks is the working database clash, so that one still shows.
    if (result.error !== WORKING_DATABASE_RULE) return null;
    return (
      <div aria-live="polite">
        {/* eslint-disable-next-line no-restricted-syntax -- not a warehouse failure: the setup SQL generator's own message about the form's values */}
        <Callout variant="info">{result.error}</Callout>
      </div>
    );
  }
  return (
    <OutputSection
      label="Setup SQL"
      description={CLICKHOUSE_SETUP_DESCRIPTION}
      action={
        <ButtonGroup>
          <DownloadButton text={result.sql} filename="clickhouse-setup.sql" />
          <CopyButton text={result.sql} label="Copy" copyLabel="Setup SQL" />
        </ButtonGroup>
      }
    >
      <SyntaxHighlighter
        language="sql"
        style={syntaxStyle}
        wrapLongLines
        codeTagProps={{ style: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } }}
        customStyle={{ ...codeBlockCustomStyle, overflowWrap: 'anywhere' }}
      >
        {result.sql}
      </SyntaxHighlighter>
    </OutputSection>
  );
}
